import { useState, useMemo } from "react";
import { Button, DatePicker, Spin, message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Column } from "@ant-design/plots";

import {
  fetchMembershipDistribution,
  fetchMembershipRetention,
} from "../../apis/statisticsApi";

import MembershipRetention from "../../components/statistics/MembershipRetention";

dayjs.extend(utc);

const GOLDEN_ANGLE = 137.508;

const colorFromId = (id) => {
  const baseHue = (id * GOLDEN_ANGLE) % 360;

  const saturationLevels = [65, 75, 85];
  const lightnessLevels = [45, 55, 65];

  const saturation = saturationLevels[id % saturationLevels.length];
  const lightness = lightnessLevels[id % lightnessLevels.length];

  const cycle = Math.floor(id / saturationLevels.length);
  const hue = (baseHue + cycle * 15) % 360;

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const MembershipDistribution = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rawChartData, setRawChartData] = useState([]);
  const [membershipRetentionData, setMembershipRetentionData] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState(["Premium", "Plus", "Lite"]);

  const fetchStats = async () => {
    if (!startDate || !endDate) {
      message.warning("Por favor selecciona ambas fechas.");
      return;
    }

    const start = dayjs.utc(startDate);
    const end = dayjs.utc(endDate);

    if (!start.isBefore(end, "day")) {
      message.warning("La fecha de inicio debe ser anterior a la fecha de fin.");
      return;
    }

    setLoading(true);

    try {
      const [retentionRes, distributionRes] = await Promise.all([
        fetchMembershipRetention(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")),
        fetchMembershipDistribution(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")),
      ]);

      setMembershipRetentionData(retentionRes.data);

      const formatted = distributionRes.data.map((d) => ({
        month: dayjs.utc(d.month_start).format("YYYY-MM"),
        planId: d.plan_id,
        plan: `${d.plan_name} (#${d.plan_id})`,
        value: d.active_users,
      }));

      setRawChartData(formatted);

    } catch (err) {
      console.error(err);
      message.error("Error loading membership distribution.");
    } finally {
      setLoading(false);
    }
  };

  const activePlanNames = useMemo(() => {
    const map = new Map();

    rawChartData.forEach(({ planId, plan }) => {
      if (planId >= 42 && !map.has(plan)) {
        map.set(plan, true);
      }
    });

    return Array.from(map.keys());
  }, [rawChartData]);

  // 🔥 AntD Column config
  const columnConfig = useMemo(() => ({
    data: rawChartData,
    xField: "month",
    yField: "value",
    seriesField: "plan",
    colorField: "plan",
    isStack: true,
    animation: false,

    color: (plan) => {
      const match = plan.match(/\(#(\d+)\)/);
      return match ? colorFromId(Number(match[1])) : "#999";
    },

    onReady: ({ chart }) => {
      try {
        chart.on('afterrender', () => {
          chart.emit('legend:filter', {
            data: { channel: 'color', values: activePlanNames },
          });
        });
      } catch (e) {
        console.error(e);
      }
    },

    interaction: {
      elementHighlightByColor: { link: true },
    },

    state: {
      active: { linkFill: 'rgba(0,0,0,0.25)', stroke: 'black', lineWidth: 0.5 },
      inactive: {
        opacity: 0.8 
      },
    },
  }), [activePlanNames, rawChartData]);

  return (
    <div className="container">
      <h2 className="text-center mb-3">Membership Distribution</h2>

      <div className="text-center" style={{ marginBottom: 16 }}>
        <DatePicker onChange={setStartDate} />{" "}
        <DatePicker onChange={setEndDate} />{" "}
        <Button onClick={fetchStats} type="primary">
          Buscar
        </Button>
      </div>

      {loading ? (
        <Spin />
      ) : (
        <div className="bg-white p-4 rounded-2xl shadow">
          <Column {...columnConfig} />

          <MembershipRetention
            data={membershipRetentionData}
            selectedPlans={selectedPlans}
            setSelectedPlans={setSelectedPlans}
          />
        </div>
      )}
    </div>
  );
};

export default MembershipDistribution;
